import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { BUNDLE_PROP, generateBundleId } from '@theme/cart-stamp-bundle';

/** @typedef {{ handle: string; title: string; code: string; iconUrl: string }} SymbolOption */

/** @typedef {{
 *   maxNameLength: number;
 *   previewEnabled: boolean;
 *   previewRatio: number;
 *   isTeacherProduct?: boolean;
 *   inkVariantId: string | number | null;
 *   schoolLabelVariantId: string | number | null;
 *   giftPackageVariantId: string | number | null;
 *   decorativeLabelVariantId: string | number | null;
 *   inkPriceCents: number;
 *   schoolLabelPriceCents: number;
 *   giftPackagePriceCents: number;
 *   decorativeLabelPriceCents: number;
 *   inkPriceText: string;
 *   schoolLabelPriceText: string;
 *   giftPackagePriceText: string;
 *   decorativeLabelPriceText: string;
 *   sectionId: string;
 *   defaultSymbol: SymbolOption | null;
 * }} PersonalizationConfig */

const PLACEHOLDER_NAME = 'ARZU YENER';
const PLACEHOLDER_TEACHER_NAME = 'ARZU';
const TEACHER_SUFFIX = 'ÖĞRETMEN';
const SAFE_NAME_PATTERN = /[^a-zA-Z0-9\s.\-\u00C7\u00E7\u011E\u011F\u0130\u0131\u00D6\u00F6\u015E\u015F\u00DC\u00FC]/g;
const MULTI_SPACE_PATTERN = /\s+/g;
const NONE_SYMBOL = { handle: 'none', title: 'Sembol Yok', code: '', iconUrl: '' };

/** Üretim yazılımı (etiket_tekli_pdf_olustur) ölçüleri — mm / pt */
const STAMP = {
  widthMm: 33,
  heightMm: 28,
  blockHeightMm: 26,
  marginTopMm: 1,
  symbolMm: 8,
  minSymbolMm: 4,
  symbolGapMm: 1,
  fontPt: 13,
  minFontPt: 9,
  lineGapRatio: 0.2,
};

/**
 * @param {number} pt
 * @param {number} pxPerMm
 */
function ptToPx(pt, pxPerMm) {
  return pt * 0.3528 * pxPerMm;
}

/**
 * @param {string} raw
 * @param {number} maxLength
 * @returns {string}
 */
function normalizeName(raw, maxLength) {
  let value = raw.replace(SAFE_NAME_PATTERN, '');
  value = value.replace(MULTI_SPACE_PATTERN, ' ').replace(/^\s+/, '');
  value = value.toLocaleUpperCase('tr-TR');
  if (maxLength > 0 && value.length > maxLength) {
    value = value.slice(0, maxLength);
  }
  return value;
}

/**
 * @param {string} name
 * @returns {[string, string]}
 */
function splitNameLines(name) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return ['', ''];

  let digitIndex = -1;
  for (let i = 0; i < words.length; i += 1) {
    if (/\d/.test(words[i])) {
      digitIndex = i;
      break;
    }
  }
  if (digitIndex !== -1) {
    return [words.slice(0, digitIndex).join(' '), words.slice(digitIndex).join(' ')];
  }
  if (words.length === 1) return [words[0], ''];
  if (words.length === 2) return [words[0], words[1]];
  return [words.slice(0, -1).join(' '), words[words.length - 1]];
}

/**
 * @typedef {object} PersonalizationRefs
 * @property {HTMLInputElement} nameInput
 * @property {HTMLElement} nameError
 * @property {HTMLElement} [surnameWarn]
 * @property {HTMLInputElement} propName
 * @property {HTMLInputElement} propSymbol
 * @property {HTMLInputElement} propSymbolCode
 * @property {HTMLInputElement} propInk
 * @property {HTMLInputElement} propSchoolLabel
 * @property {HTMLInputElement} propGiftPackage
 * @property {HTMLInputElement} propDecorativeLabel
 * @property {HTMLInputElement} [noSymbolCheckbox]
 * @property {HTMLInputElement} [inkCheckbox]
 * @property {HTMLInputElement} [schoolLabelCheckbox]
 * @property {HTMLInputElement} [giftPackageCheckbox]
 * @property {HTMLInputElement} [decorativeLabelCheckbox]
 * @property {HTMLElement} [schoolLabelCard]
 * @property {HTMLElement} [giftPackageCard]
 * @property {HTMLElement} [decorativeLabelCard]
 * @property {HTMLElement} [inkPriceLabel]
 * @property {HTMLElement} [schoolPriceLabel]
 * @property {HTMLElement} [giftPriceLabel]
 * @property {HTMLElement} [decorativePriceLabel]
 * @property {HTMLElement} [upsellSummary]
 * @property {HTMLElement} [upsellSummaryText]
 * @property {HTMLElement} [previewCard]
 * @property {HTMLElement} [previewSymbol]
 * @property {HTMLImageElement} [previewIcon]
 * @property {HTMLElement} [previewName]
 * @property {HTMLElement} [previewNameLine1]
 * @property {HTMLElement} [previewNameLine2]
 */

/**
 * @extends {Component<PersonalizationRefs>}
 */
class ProductPersonalizationComponent extends Component {
  /** @type {PersonalizationConfig | null} */
  #config = null;

  /** @type {SymbolOption | null} */
  #activeSymbol = null;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#loadConfig();
    this.#applyDefaultSymbol();
    this.#syncProperties();
    this.#updateAllPreviews();
    this.#updateUpsellSummary();
    void this.#hydrateUpsellPrices();

    this.refs.nameInput?.addEventListener('input', this.#onNameInput, { signal });
    this.refs.nameInput?.addEventListener('blur', this.#onNameBlur, { signal });
    this.refs.noSymbolCheckbox?.addEventListener('change', this.#onSymbolToggle, { signal });
    this.refs.inkCheckbox?.addEventListener('change', this.#onUpsellChange, { signal });
    this.refs.schoolLabelCheckbox?.addEventListener('change', this.#onSchoolLabelChange, { signal });
    this.refs.giftPackageCheckbox?.addEventListener('change', this.#onGiftPackageChange, { signal });
    this.refs.decorativeLabelCheckbox?.addEventListener('change', this.#onDecorativeLabelChange, { signal });

    this.closest('form')?.addEventListener('keydown', this.#onFormKeydown, { signal, capture: true });

    window.addEventListener('resize', this.#onResize, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  #loadConfig() {
    const configEl = this.querySelector('[data-config]');
    if (!(configEl instanceof HTMLScriptElement)) return;

    try {
      /** @type {PersonalizationConfig} */
      const parsed = JSON.parse(configEl.textContent || '{}');
      if (parsed.defaultSymbol && !parsed.defaultSymbol.handle) {
        parsed.defaultSymbol = null;
      }
      this.#config = parsed;
    } catch (error) {
      console.error('Personalization config parse error:', error);
      this.#config = {
        maxNameLength: 24,
        previewEnabled: true,
        previewRatio: 33 / 28,
        isTeacherProduct: this.hasAttribute('data-teacher-product'),
        inkVariantId: null,
        schoolLabelVariantId: null,
        giftPackageVariantId: null,
        decorativeLabelVariantId: null,
        inkPriceCents: 0,
        schoolLabelPriceCents: 0,
        giftPackagePriceCents: 0,
        decorativeLabelPriceCents: 0,
        inkPriceText: '',
        schoolLabelPriceText: '',
        giftPackagePriceText: '',
        decorativeLabelPriceText: '',
        sectionId: this.dataset.sectionId || '',
        defaultSymbol: null,
      };
    }
  }

  #applyDefaultSymbol() {
    const wantsNoSymbol = this.refs.noSymbolCheckbox?.checked;
    const defaultSymbol = this.#config?.defaultSymbol;
    this.#activeSymbol = wantsNoSymbol || !defaultSymbol?.handle ? NONE_SYMBOL : defaultSymbol;
  }

  #onSymbolToggle = () => {
    this.#applyDefaultSymbol();
    this.#syncProperties();
    this.#updateAllPreviews();
  };

  #onNameInput = () => {
    const maxLength = this.#config?.maxNameLength ?? 24;
    const { nameInput } = this.refs;
    const cursorAtEnd = nameInput.selectionStart === nameInput.value.length;
    const normalized = normalizeName(nameInput.value, maxLength);

    if (nameInput.value !== normalized) {
      nameInput.value = normalized;
      if (cursorAtEnd) nameInput.setSelectionRange(normalized.length, normalized.length);
    }

    this.#clearNameError();
    this.#syncTeacherSurnameWarn();
    this.#syncProperties();
    this.#updateAllPreviews();
  };

  #onNameBlur = () => {
    const { nameInput } = this.refs;
    nameInput.value = normalizeName(nameInput.value, this.#config?.maxNameLength ?? 24);
    this.#syncTeacherSurnameWarn();
    this.#syncProperties();
    this.#updateAllPreviews();
  };

  /** @returns {boolean} */
  #isTeacherProduct() {
    return Boolean(this.#config?.isTeacherProduct) || this.hasAttribute('data-teacher-product');
  }

  /** @param {string} name */
  #hasMultipleNameWords(name) {
    return name.trim().split(/\s+/).filter(Boolean).length > 1;
  }

  #syncTeacherSurnameWarn() {
    const warn = this.refs.surnameWarn;
    if (!warn || !this.#isTeacherProduct()) return;

    const hasSurname = this.#hasMultipleNameWords(this.#getNormalizedName());
    warn.classList.toggle('hidden', !hasSurname);
    this.refs.nameInput?.classList.toggle('product-personalization__input--surname-warn', hasSurname);
  }

  /** @param {KeyboardEvent} event */
  #onFormKeydown = (event) => {
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target instanceof HTMLButtonElement) return;
    if (target instanceof HTMLTextAreaElement) return;

    if (target instanceof HTMLInputElement) {
      const type = target.type;
      if (type === 'submit' || type === 'button') return;
      if (type === 'checkbox' || type === 'radio') return;
    }

    event.preventDefault();
  };

  #onUpsellChange = () => {
    this.#syncProperties();
    this.#updateUpsellSummary();
  };

  #onSchoolLabelChange = () => {
    const checked = this.refs.schoolLabelCheckbox?.checked ?? false;
    this.refs.schoolLabelCard?.classList.toggle('product-personalization__school-card--active', checked);
    this.#syncProperties();
    this.#updateUpsellSummary();
  };

  #onGiftPackageChange = () => {
    const checked = this.refs.giftPackageCheckbox?.checked ?? false;
    this.refs.giftPackageCard?.classList.toggle('product-personalization__school-card--active', checked);
    this.#syncProperties();
    this.#updateUpsellSummary();
  };

  #onDecorativeLabelChange = () => {
    const checked = this.refs.decorativeLabelCheckbox?.checked ?? false;
    this.refs.decorativeLabelCard?.classList.toggle('product-personalization__school-card--active', checked);
    this.#syncProperties();
    this.#updateUpsellSummary();
  };

  #onResize = () => {
    this.#layoutStampPreview();
  };

  /** @returns {string} */
  #getNormalizedName() {
    return normalizeName(this.refs.nameInput?.value || '', this.#config?.maxNameLength ?? 24);
  }

  #syncProperties() {
    const name = this.#getNormalizedName();
    const symbol = this.#activeSymbol;

    if (this.refs.propName) this.refs.propName.value = name;
    if (this.refs.propSymbol) this.refs.propSymbol.value = symbol?.title || '';
    if (this.refs.propSymbolCode) this.refs.propSymbolCode.value = symbol?.code || '';

    this.#setOptionalProperty(this.refs.propInk, 'Yedek Mürekkep', this.refs.inkCheckbox?.checked);
    this.#setOptionalProperty(this.refs.propSchoolLabel, 'Okul Etiketi', this.refs.schoolLabelCheckbox?.checked);
    this.#setOptionalProperty(this.refs.propGiftPackage, 'Hediye Paketi', this.refs.giftPackageCheckbox?.checked);
    this.#setOptionalProperty(this.refs.propDecorativeLabel, 'Dekoratif Etiket Seti', this.refs.decorativeLabelCheckbox?.checked);
  }

  /**
   * @param {HTMLInputElement | undefined} input
   * @param {string} key
   * @param {boolean | undefined} active
   */
  #setOptionalProperty(input, key, active) {
    if (!input) return;
    if (active) {
      input.name = `properties[${key}]`;
      input.value = 'Evet';
    } else {
      input.removeAttribute('name');
      input.value = '';
    }
  }

  #updateAllPreviews() {
    this.#updateStampPreview();
  }

  /** @param {number} cents */
  #formatUpsellMoney(cents) {
    const amount = cents / 100;
    const formatted = amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `+${formatted} ₺`;
  }

  /**
   * Live price from /variants/{id}.js when Liquid only has a raw variant ID.
   * @param {number} variantId
   * @returns {Promise<number>}
   */
  async #fetchVariantPriceCents(variantId) {
    try {
      const response = await fetch(`/variants/${variantId}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });
      if (!response.ok) return 0;
      const data = await response.json();
      const price = Number(data?.price);
      return Number.isFinite(price) && price > 0 ? price : 0;
    } catch {
      return 0;
    }
  }

  /**
   * @param {'ink' | 'school' | 'gift' | 'decorative'} type
   * @param {number} cents
   */
  #applyUpsellPrice(type, cents) {
    if (!this.#config || !(cents > 0)) return;

    const text = this.#formatUpsellMoney(cents);
    if (type === 'ink') {
      this.#config.inkPriceCents = cents;
      this.#config.inkPriceText = text;
      if (this.refs.inkPriceLabel) this.refs.inkPriceLabel.textContent = text;
    } else if (type === 'school') {
      this.#config.schoolLabelPriceCents = cents;
      this.#config.schoolLabelPriceText = text;
      if (this.refs.schoolPriceLabel) this.refs.schoolPriceLabel.textContent = text;
    } else if (type === 'gift') {
      this.#config.giftPackagePriceCents = cents;
      this.#config.giftPackagePriceText = text;
      if (this.refs.giftPriceLabel) this.refs.giftPriceLabel.textContent = text;
    } else {
      this.#config.decorativeLabelPriceCents = cents;
      this.#config.decorativeLabelPriceText = text;
      if (this.refs.decorativePriceLabel) this.refs.decorativePriceLabel.textContent = text;
    }
  }

  async #hydrateUpsellPrices() {
    /** @type {Array<{ type: 'ink' | 'school' | 'gift' | 'decorative'; id: number }>} */
    const targets = [
      { type: 'ink', id: Number(this.#config?.inkVariantId) },
      { type: 'school', id: Number(this.#config?.schoolLabelVariantId) },
      { type: 'gift', id: Number(this.#config?.giftPackageVariantId) },
      { type: 'decorative', id: Number(this.#config?.decorativeLabelVariantId) },
    ];

    await Promise.all(
      targets.map(async ({ type, id }) => {
        if (!Number.isFinite(id) || id <= 0) return;
        if (this.#getUpsellPriceCents(type) > 0) return;
        const cents = await this.#fetchVariantPriceCents(id);
        this.#applyUpsellPrice(type, cents);
      })
    );

    this.#updateUpsellSummary();
  }

  /** @param {string | undefined} text */
  #parsePriceText(text) {
    if (!text) return 0;
    const match = text.match(/(\d+)[,.](\d{2})/);
    if (!match) return 0;
    return parseInt(match[1], 10) * 100 + parseInt(match[2], 10);
  }

  /**
   * @param {'ink' | 'school' | 'gift' | 'decorative'} type
   * @returns {number}
   */
  #getUpsellPriceCents(type) {
    /** @type {Record<'ink' | 'school' | 'gift' | 'decorative', number | undefined>} */
    const centsMap = {
      ink: this.#config?.inkPriceCents,
      school: this.#config?.schoolLabelPriceCents,
      gift: this.#config?.giftPackagePriceCents,
      decorative: this.#config?.decorativeLabelPriceCents,
    };
    /** @type {Record<'ink' | 'school' | 'gift' | 'decorative', string | undefined>} */
    const textMap = {
      ink: this.#config?.inkPriceText,
      school: this.#config?.schoolLabelPriceText,
      gift: this.#config?.giftPackagePriceText,
      decorative: this.#config?.decorativeLabelPriceText,
    };
    const cents = centsMap[type];
    if (cents && cents > 0) return cents;
    return this.#parsePriceText(textMap[type]);
  }

  /**
   * @param {string[]} labels
   * @returns {string}
   */
  #joinTurkishLabels(labels) {
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} ve ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')} ve ${labels[labels.length - 1]}`;
  }

  #updateUpsellSummary() {
    const summary = this.refs.upsellSummary;
    const summaryText = this.refs.upsellSummaryText;
    if (!summary || !summaryText) return;

    const inkSelected = Boolean(this.refs.inkCheckbox?.checked);
    const schoolSelected = Boolean(this.refs.schoolLabelCheckbox?.checked);
    const giftSelected = Boolean(this.refs.giftPackageCheckbox?.checked);
    const decorativeSelected = Boolean(this.refs.decorativeLabelCheckbox?.checked);

    if (!inkSelected && !schoolSelected && !giftSelected && !decorativeSelected) {
      summary.classList.add('hidden');
      summaryText.textContent = '';
      return;
    }

    /** @type {string[]} */
    const labels = [];
    let totalCents = 0;
    if (inkSelected) {
      labels.push('Yedek Mürekkep');
      totalCents += this.#getUpsellPriceCents('ink');
    }
    if (schoolSelected) {
      labels.push('Okul Etiketi');
      totalCents += this.#getUpsellPriceCents('school');
    }
    if (giftSelected) {
      labels.push('Hediye Paketi');
      totalCents += this.#getUpsellPriceCents('gift');
    }
    if (decorativeSelected) {
      labels.push('Dekoratif Etiket Seti');
      totalCents += this.#getUpsellPriceCents('decorative');
    }

    const joined = this.#joinTurkishLabels(labels);
    const totalText = totalCents > 0 ? this.#formatUpsellMoney(totalCents) : '';
    /** @type {string[]} */
    const notes = [];
    if (inkSelected) notes.push('Yedek mürekkep 5 ml şişe olarak gönderilir');
    if (schoolSelected) notes.push('girdiğiniz isim okul etiketlerine yazılır');
    if (giftSelected) notes.push('siparişiniz hediye paketi olarak hazırlanır');
    if (decorativeSelected) notes.push('dekoratif etiket seti siparişinize eklenir');

    const noteText = notes.length ? `. ${notes.map((n, i) => (i === 0 ? n.charAt(0).toUpperCase() + n.slice(1) : n)).join('; ')}.` : '.';

    if (labels.length === 1 && inkSelected) {
      summaryText.textContent = totalText
        ? `Yedek Mürekkep eklediniz — sepet tutarınıza ${totalText} eklenecektir. 5 ml yedek mürekkep şişesi olarak gönderilir.`
        : 'Yedek Mürekkep eklediniz. 5 ml yedek mürekkep şişesi olarak gönderilir.';
    } else if (labels.length === 1 && schoolSelected) {
      summaryText.textContent = totalText
        ? `Okul Etiketi eklediniz — sepet tutarınıza ${totalText} eklenecektir. Girdiğiniz isim etiketlere otomatik yazılır.`
        : 'Okul Etiketi eklediniz. Girdiğiniz isim etiketlere otomatik yazılır.';
    } else if (labels.length === 1 && giftSelected) {
      summaryText.textContent = totalText
        ? `Hediye Paketi eklediniz — sepet tutarınıza ${totalText} eklenecektir. Siparişiniz hediye paketi olarak hazırlanıp gönderilir.`
        : 'Hediye Paketi eklediniz. Siparişiniz hediye paketi olarak hazırlanıp gönderilir.';
    } else if (labels.length === 1 && decorativeSelected) {
      summaryText.textContent = totalText
        ? `Dekoratif Etiket Seti eklediniz — sepet tutarınıza ${totalText} eklenecektir. Set siparişinizle birlikte gönderilir.`
        : 'Dekoratif Etiket Seti eklediniz. Set siparişinizle birlikte gönderilir.';
    } else {
      summaryText.textContent = totalText
        ? `Harika seçim! ${joined} ile birlikte sepet tutarınıza toplam ${totalText} eklenecektir${noteText}`
        : `${joined} seçtiniz${noteText}`;
    }

    summary.classList.remove('hidden');
  }

  #updateStampPreview() {
    if (!this.#config?.previewEnabled) return;

    const name = this.#getNormalizedName();
    const symbol = this.#activeSymbol;
    const noSymbol = !symbol || symbol.handle === 'none' || !symbol.iconUrl;
    const isTeacher = this.#isTeacherProduct();
    const valueForDisplay = name || (isTeacher ? PLACEHOLDER_TEACHER_NAME : PLACEHOLDER_NAME);

    /** @type {string} */
    let line1;
    /** @type {string} */
    let line2;

    if (isTeacher) {
      line1 = valueForDisplay;
      line2 = TEACHER_SUFFIX;
    } else {
      [line1, line2] = splitNameLines(valueForDisplay);
    }

    this.refs.previewCard?.classList.toggle('product-personalization__preview--no-symbol', noSymbol);
    this.refs.previewSymbol?.classList.toggle('hidden', noSymbol);

    if (this.refs.previewNameLine1) this.refs.previewNameLine1.textContent = line1;
    if (this.refs.previewNameLine2) {
      this.refs.previewNameLine2.textContent = line2;
      this.refs.previewNameLine2.style.display = line2 ? 'block' : 'none';
    }
    this.refs.previewName?.classList.toggle('product-personalization__preview-name--placeholder', !name);

    const showIcon = symbol && symbol.handle !== 'none' && symbol.iconUrl;
    if (this.refs.previewIcon) {
      if (showIcon) {
        this.refs.previewIcon.onload = () => this.#layoutStampPreview();
        this.refs.previewIcon.src = symbol.iconUrl;
        this.refs.previewIcon.classList.remove('hidden');
      } else {
        this.refs.previewIcon.onload = null;
        this.refs.previewIcon.classList.add('hidden');
        this.refs.previewIcon.removeAttribute('src');
      }
    }

    this.#layoutStampPreview();
  }

  #layoutStampPreview() {
    const card = this.refs.previewCard;
    const nameEl = this.refs.previewName;
    const symbolEl = this.refs.previewSymbol;
    const icon = this.refs.previewIcon;
    if (!card || !nameEl) return;

    const cardW = card.clientWidth;
    if (cardW <= 0) return;

    const pxPerMm = cardW / STAMP.widthMm;
    const maxContentHeightPx = STAMP.blockHeightMm * pxPerMm;
    const gapPx = STAMP.symbolGapMm * pxPerMm;
    const hasSymbol = Boolean(symbolEl && !symbolEl.classList.contains('hidden') && icon && !icon.classList.contains('hidden'));

    const lineEls = /** @type {HTMLElement[]} */ (
      Array.from(nameEl.querySelectorAll('.product-personalization__preview-name-line')).filter(
        (node) => /** @type {HTMLElement} */ (node).style.display !== 'none'
      )
    );
    const lineCount = Math.max(lineEls.length, 1);

    let fontPt = STAMP.fontPt;
    let symbolMm = STAMP.symbolMm;

    while (true) {
      const fontPx = ptToPx(fontPt, pxPerMm);
      const lineStep = fontPx * (1 + STAMP.lineGapRatio);
      const textHeightPx = lineCount === 1 ? fontPx : fontPx + (lineCount - 1) * lineStep;
      const symbolPx = symbolMm * pxPerMm;
      const totalHeightPx = hasSymbol ? symbolPx + gapPx + textHeightPx : textHeightPx;

      if (totalHeightPx <= maxContentHeightPx || (fontPt <= STAMP.minFontPt && symbolMm <= STAMP.minSymbolMm)) {
        break;
      }
      if (fontPt > STAMP.minFontPt) {
        fontPt -= 0.5;
      } else if (symbolMm > STAMP.minSymbolMm) {
        symbolMm -= 0.5;
      } else {
        break;
      }
    }

    let fontPx = ptToPx(fontPt, pxPerMm);
    nameEl.style.fontSize = `${fontPx}px`;
    nameEl.style.lineHeight = String(1 + STAMP.lineGapRatio);
    nameEl.style.marginTop = '0';

    const maxTextWidth = cardW;
    const shrinkTextToFit = () => {
      let fits = true;
      for (const line of lineEls) {
        if (line.scrollWidth > maxTextWidth) {
          fits = false;
          break;
        }
      }
      return fits;
    };

    while (!shrinkTextToFit() && fontPt > STAMP.minFontPt) {
      fontPt -= 0.5;
      fontPx = ptToPx(fontPt, pxPerMm);
      nameEl.style.fontSize = `${fontPx}px`;
    }

    if (hasSymbol && symbolEl && icon) {
      const symbolPx = symbolMm * pxPerMm;
      symbolEl.style.flex = `0 0 ${symbolPx}px`;
      symbolEl.style.height = `${symbolPx}px`;
      symbolEl.style.maxHeight = `${symbolPx}px`;

      const naturalW = icon.naturalWidth;
      const naturalH = icon.naturalHeight;
      if (naturalW > 0 && naturalH > 0) {
        const ratio = naturalW / naturalH;
        let drawW;
        let drawH;
        if (ratio >= 1) {
          drawW = symbolPx;
          drawH = symbolPx / ratio;
        } else {
          drawW = symbolPx * ratio;
          drawH = symbolPx;
        }
        icon.style.width = `${drawW}px`;
        icon.style.height = `${drawH}px`;
      } else {
        icon.style.width = 'auto';
        icon.style.height = `${symbolPx}px`;
      }
    } else if (symbolEl) {
      symbolEl.style.flex = '';
      symbolEl.style.height = '';
      symbolEl.style.maxHeight = '';
    }

    card.style.removeProperty('--pp-margin-top');
    card.style.setProperty('--pp-content-gap', hasSymbol ? `${gapPx}px` : '0');
  }

  /** @returns {boolean} */
  #validate() {
    const name = this.#getNormalizedName();
    if (!name.length) {
      this.#setNameErrorMessage(
        this.#isTeacherProduct()
          ? 'Lütfen sadece öğretmen adını giriniz (soyisim olmadan).'
          : 'Lütfen geçerli bir isim soyisim giriniz.'
      );
      this.#showNameError();
      this.refs.nameInput?.classList.add('product-personalization__input--error');
      this.refs.nameInput?.focus();
      return false;
    }

    if (this.#isTeacherProduct() && this.#hasMultipleNameWords(name)) {
      this.#syncTeacherSurnameWarn();
      this.#setNameErrorMessage(
        'Soyisim yazmayın. Sadece öğretmenin adını girin — örn. Sevil → Sevil Öğretmen.'
      );
      this.#showNameError();
      this.refs.nameInput?.classList.add('product-personalization__input--error');
      this.refs.nameInput?.focus();
      return false;
    }

    this.#clearNameError();
    return true;
  }

  /** @param {string} message */
  #setNameErrorMessage(message) {
    if (this.refs.nameError) this.refs.nameError.textContent = message;
  }

  #showNameError() {
    this.refs.nameError?.classList.remove('hidden');
    const liveRegion = this.closest('product-form-component')?.querySelector('[ref="liveRegion"]');
    if (liveRegion && this.refs.nameError) {
      liveRegion.textContent = this.refs.nameError.textContent || '';
    }
  }

  #clearNameError() {
    this.refs.nameError?.classList.add('hidden');
    this.refs.nameInput?.classList.remove('product-personalization__input--error');
  }

  /**
   * @param {string} [bundleId]
   * @returns {Array<{ id: number; quantity: number; properties: Record<string, string> }>}
   */
  #getUpsellItems(bundleId) {
    /** @type {Array<{ id: number; quantity: number; properties: Record<string, string> }>} */
    const items = [];
    const name = this.#getNormalizedName();
    const bundleProps = bundleId ? { [BUNDLE_PROP]: bundleId } : {};

    const inkId = Number(this.#config?.inkVariantId);
    if (this.refs.inkCheckbox?.checked && Number.isFinite(inkId) && inkId > 0) {
      items.push({
        id: inkId,
        quantity: 1,
        properties: {
          ...bundleProps,
          '_Bağlı Ana Ürün': 'Evet',
          '_Ürün Tipi': 'Yedek Mürekkep Upsell',
        },
      });
    }

    const schoolId = Number(this.#config?.schoolLabelVariantId);
    if (this.refs.schoolLabelCheckbox?.checked && Number.isFinite(schoolId) && schoolId > 0) {
      items.push({
        id: schoolId,
        quantity: 1,
        properties: {
          ...bundleProps,
          '_Bağlı Ana Ürün': 'Evet',
          'İsim Soyisim': name,
          '_Ürün Tipi': 'Okul Etiketi Upsell',
        },
      });
    }

    const giftId = Number(this.#config?.giftPackageVariantId);
    if (this.refs.giftPackageCheckbox?.checked && Number.isFinite(giftId) && giftId > 0) {
      items.push({
        id: giftId,
        quantity: 1,
        properties: {
          ...bundleProps,
          '_Bağlı Ana Ürün': 'Evet',
          '_Ürün Tipi': 'Hediye Paketi Upsell',
        },
      });
    }

    const decorativeId = Number(this.#config?.decorativeLabelVariantId);
    if (this.refs.decorativeLabelCheckbox?.checked && Number.isFinite(decorativeId) && decorativeId > 0) {
      items.push({
        id: decorativeId,
        quantity: 1,
        properties: {
          ...bundleProps,
          '_Bağlı Ana Ürün': 'Evet',
          '_Ürün Tipi': 'Dekoratif Etiket Seti Upsell',
        },
      });
    }

    return items;
  }

  /** @returns {string[]} */
  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
    });
    return Array.from(ids);
  }

  /**
   * @param {FormData} formData
   * @returns {Record<string, string>}
   */
  #extractProperties(formData) {
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && typeof value === 'string') {
        properties[key.slice(11, -1)] = value;
      }
    }
    return properties;
  }

  /**
   * Called before add-to-cart animations. Returns false when personalization blocks add.
   * @returns {boolean}
   */
  canAddToCart() {
    this.#syncProperties();
    return this.#validate();
  }

  /**
   * Called from product-form before default add-to-cart. Returns true when handled here.
   * @param {Event} event
   * @returns {boolean}
   */
  interceptSubmit(event) {
    this.#syncProperties();

    if (!this.#validate()) {
      event.preventDefault();
      return true;
    }

    const upsellItems = this.#getUpsellItems();
    if (upsellItems.length === 0) return false;

    event.preventDefault();
    void this.#batchAddToCart();
    return true;
  }

  async #batchAddToCart() {
    if (this.#isSubmitting) return;

    const form = this.closest('form');
    const productForm = this.closest('product-form-component');
    if (!form || !productForm) return;

    const bundleId = generateBundleId();
    const upsellItems = this.#getUpsellItems(bundleId);
    const allAddToCartContainers = productForm.querySelectorAll('add-to-cart-component');
    /** @type {Map<HTMLButtonElement, boolean>} */
    const initialDisabled = new Map();
    allAddToCartContainers.forEach((container) => {
      const btn = container.querySelector('[ref="addToCartButton"]');
      if (btn instanceof HTMLButtonElement) {
        initialDisabled.set(btn, btn.disabled);
        btn.disabled = true;
      }
    });

    this.#isSubmitting = true;
    const { addToCartTextError } = productForm.refs || {};

    try {
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = Number(formData.get('quantity')) || Number(productForm.dataset.quantityDefault) || 1;
      const properties = { ...this.#extractProperties(formData), [BUNDLE_PROP]: bundleId };
      if (!variantId) throw new Error('Variant ID missing');

      const sectionIds = this.#getSectionIds();
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add';
      const addUrl = cartAddUrl.includes('.js') ? cartAddUrl : `${cartAddUrl}.js`;

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity, properties }, ...upsellItems],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (data.status) {
        productForm.dispatchEvent(
          new CartErrorEvent(form.id || '', data.message || 'Sepete eklenemedi', data.description, data.errors)
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) textNode.textContent = data.message;
        }
        return;
      }

      if (addToCartTextError) addToCartTextError.classList.add('hidden');

      const addedCount = quantity + upsellItems.reduce((sum, item) => sum + item.quantity, 0);
      productForm.dispatchEvent(
        new CartAddEvent(data, String(variantId), {
          source: 'product-form-component',
          itemCount: addedCount,
          productId: productForm.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Personalization add to cart error:', error);
    } finally {
      this.#isSubmitting = false;
      allAddToCartContainers.forEach((container) => {
        const btn = container.querySelector('[ref="addToCartButton"]');
        if (btn instanceof HTMLButtonElement) btn.disabled = initialDisabled.get(btn) ?? false;
      });
    }
  }
}

if (!customElements.get('product-personalization-component')) {
  customElements.define('product-personalization-component', ProductPersonalizationComponent);
}
